import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  RefreshCw, Info, AlertTriangle, Image as ImageIcon, Type, Trophy, Lightbulb, ChevronDown, Zap,
} from 'lucide-react';

/**
 * Creative & Copy Insights — "hangi görsel ve metin dönüştürüyor?" tek sayfa cevap.
 *
 * Sayfa marka bağımsız: seçili markanın Meta yaratıcıları (veritabanı + Graph'tan
 * canlı metin) ile Google Ads metin varlıkları (canlı API) yan yana gelir.
 * Tasarım Glowy Lens ile aynı dil: sticky üst çubuk, beyaz rounded-2xl kartlar, İngilizce.
 *
 * 🔑 Üstteki bulgu kartları veriden ÜRETİLİYOR (backend `verdicts`), elle yazılmıyor;
 *    hacim eşiğini geçmeyen kova "kazandı" diye sunulmuyor, "not enough data" yazıyor.
 * 🔑 Görsel küçük tutuluyor — sayfa galeri değil, karar tablosu. Tam boy hâli
 *    tıklanınca yeni sekmede açılır.
 */

const API_BASE = 'https://api.glowytics.com';
const PAGE_ROWS = 20;
const PERIODS = [30, 90, 180, 365];

const fmtInt = (n) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('en-US'));
const fmtMoney = (n) => (n == null ? '—' : `₺${Math.round(Number(n)).toLocaleString('en-US')}`);
const fmtPct = (n, d = 2) => (n == null ? '—' : `${(Number(n) * 100).toFixed(d)}%`);
const fmtX = (n) => (n == null ? '—' : `${Number(n).toFixed(2)}x`);
const fmtDate = (ymd) => {
  if (!ymd) return '';
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? ymd : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const TYPE_STYLE = {
  'Catalog (DPA)': { bar: 'bg-sky-500', badge: 'bg-sky-50 text-sky-700', short: 'Catalog' },
  'Static image': { bar: 'bg-amber-400', badge: 'bg-amber-50 text-amber-700', short: 'Static' },
  Video: { bar: 'bg-violet-500', badge: 'bg-violet-50 text-violet-700', short: 'Video' },
};

/**
 * Meta yaratıcı adları çoğunlukla "{{product.name}} 2026-07-23-5e5057…" şablonu:
 * okunacak bir şey yok. Şablon etiketlerini ve tarih-hash damgasını ayıklayıp
 * başlık + oluşturulma tarihine çeviriyoruz; ad boş kalırsa reklam metninin ilk parçası.
 */
const TPL = /\{\{[^}]*\}\}/g;
const STAMP = /(\d{4}-\d{2}-\d{2})-[0-9a-f]{6,}\S*/i;
function cleanCreative(c) {
  const raw = c.name || '';
  const stamp = raw.match(STAMP);
  let title = raw.replace(STAMP, '').replace(TPL, '').replace(/[\s|·-]+$/, '').replace(/\s+/g, ' ').trim();
  let segments = String(c.text || '').replace(TPL, '').split('·').map((s) => s.trim()).filter(Boolean);
  if (!title && segments.length) title = segments[0];
  if (segments[0] === title) segments = segments.slice(1);
  return {
    title: title || `Creative ${String(c.id).slice(-6)}`,
    text: segments.join(' · '),
    date: stamp ? fmtDate(stamp[1]) : null,
  };
}

/**
 * Yaratıcı küçük görseli. Meta CDN adresleri saatlerde ölüyor, o yüzden görsel
 * backend vekilinden çekiliyor; <img src> başlık gönderemediği için istek
 * fetch ile yapılıp blob'a çevriliyor. Görünür olmadan yüklenmiyor.
 */
function Thumb({ creativeId, brandId, alt }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || !brandId) return undefined;
    let url = null;
    let cancelled = false;
    const io = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      try {
        const res = await fetch(`${API_BASE}/api/creative-performance/image/${creativeId}?brandId=${brandId}`, {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId },
        });
        if (!res.ok) throw new Error('missing');
        const blob = await res.blob();
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      } catch (_) {
        if (!cancelled) setFailed(true);
      }
    }, { rootMargin: '200px' });
    io.observe(ref.current);
    return () => { cancelled = true; io.disconnect(); if (url) URL.revokeObjectURL(url); };
  }, [creativeId, brandId]);

  return (
    <div ref={ref} className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center ring-1 ring-gray-200/60">
      {src
        ? <img src={src} alt={alt || ''} className="w-full h-full object-cover" />
        : <ImageIcon className={`w-4 h-4 ${failed ? 'text-gray-300' : 'text-gray-200 animate-pulse'}`} />}
    </div>
  );
}

const Panel = ({ className = '', children }) => (
  <div className={`rounded-2xl border border-gray-200 bg-white shadow-sm ${className}`}>{children}</div>
);

function Stat({ label, value, sub }) {
  return (
    <Panel className="px-4 py-3">
      <div className="text-sm font-medium text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums tracking-tight">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-400 truncate">{sub}</div>}
    </Panel>
  );
}

/** Bulgu kartı: kazanan + karşılaştırma. Veri yetmiyorsa iddia YOK, sebebi yazılı. */
function Finding({ v, fmt }) {
  const [platform, question] = v.title.split(/:\s*/, 2);
  return (
    <Panel className={`p-4 flex flex-col ${v.ready ? '' : 'bg-gray-50/60 shadow-none'}`}>
      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${platform === 'Meta' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}`}>{platform}</span>
        <span className="truncate">{question}</span>
      </div>
      {v.ready ? (
        <>
          <div className="mt-2 flex items-start gap-1.5 text-base font-semibold text-gray-900">
            <Trophy className="w-4 h-4 mt-1 shrink-0 text-amber-500" />
            <span>{v.winner}</span>
          </div>
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="font-semibold tabular-nums text-gray-900">{fmt(v.value)}</span>
            <span className="text-gray-400">vs</span>
            <span className="tabular-nums text-gray-500 truncate">{fmt(v.loserValue)} <span className="text-gray-400">{v.loser}</span></span>
          </div>
          <div className="mt-auto pt-3 flex items-center justify-between text-[11px] text-gray-400">
            <span>{v.detail}</span>
            {v.ratio && v.ratio > 1.05 && (
              <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">{v.ratio.toFixed(1)}x better</span>
            )}
          </div>
        </>
      ) : (
        <div className="mt-2 text-sm text-gray-400">Not enough data yet to call a winner.</div>
      )}
    </Panel>
  );
}

/** Meta harcamasının tür dağılımı: tek yatay çubuk + yanında tür satırları. */
function SpendMix({ types }) {
  if (!types?.length) return null;
  const rows = [...types].sort((a, b) => b.cost - a.cost);
  return (
    <Panel className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <h3 className="text-sm font-medium text-gray-800 shrink-0">Where Meta spend goes</h3>
        <div className="flex h-2.5 flex-1 min-w-[160px] overflow-hidden rounded-full bg-gray-100">
          {rows.map((t) => (
            <div key={t.key} className={TYPE_STYLE[t.key]?.bar || 'bg-gray-400'} style={{ width: `${(t.share || 0) * 100}%` }} title={`${t.key}: ${fmtPct(t.share, 1)}`} />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
          {rows.map((t) => (
            <span key={t.key} className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <span className={`w-2 h-2 rounded-full ${TYPE_STYLE[t.key]?.bar || 'bg-gray-400'}`} />
              <span className="text-gray-600">{t.key}</span>
              <span className="tabular-nums text-gray-400">{fmtPct(t.share, 1)}</span>
              <span className="tabular-nums text-gray-900">{fmtMoney(t.cost)}</span>
              <span className="tabular-nums font-semibold text-gray-900">{fmtX(t.roas)} ROAS</span>
            </span>
          ))}
        </div>
      </div>
    </Panel>
  );
}

/** Kova tablosu — örüntü karşılaştırmaları burada. */
function BucketTable({ title, rows, columns, note }) {
  if (!rows?.length) return null;
  return (
    <Panel className="overflow-hidden">
      <div className="px-4 pt-3 pb-2">
        <h3 className="text-sm font-medium text-gray-800">{title}</h3>
        {note && <p className="text-xs text-gray-400 mt-0.5">{note}</p>}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-xs text-gray-500 border-y border-gray-100 bg-gray-50/60">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Group</th>
              {columns.map((c) => <th key={c.key} className="px-3 py-2 text-right font-medium whitespace-nowrap">{c.label}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.key} className={r.yeterli ? '' : 'text-gray-400'}>
                <td className="px-4 py-2 text-gray-800 whitespace-nowrap">
                  {r.key}
                  {!r.yeterli && (
                    <span className="ml-2 px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-500 text-[10px] font-medium" title="Below the volume threshold; not used for findings.">low data</span>
                  )}
                </td>
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2 text-right tabular-nums text-gray-700">{c.fmt(r[c.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

const BUCKET_COLS_GOOGLE = [
  { key: 'count', label: 'Assets', fmt: fmtInt },
  { key: 'impressions', label: 'Impressions', fmt: fmtInt },
  { key: 'ctr', label: 'CTR', fmt: (v) => fmtPct(v) },
  { key: 'cvr', label: 'Conv. rate', fmt: (v) => fmtPct(v) },
  { key: 'cpa', label: 'Cost / conv.', fmt: fmtMoney },
];
const BUCKET_COLS_META_TYPE = [
  { key: 'count', label: 'Creatives', fmt: fmtInt },
  { key: 'cost', label: 'Spend', fmt: fmtMoney },
  { key: 'share', label: 'Share', fmt: (v) => fmtPct(v, 1) },
  { key: 'revenue', label: 'Revenue', fmt: fmtMoney },
  { key: 'ctr', label: 'CTR', fmt: (v) => fmtPct(v) },
  { key: 'conversions', label: 'Purchases', fmt: fmtInt },
  { key: 'cpa', label: 'CPA', fmt: fmtMoney },
  { key: 'roas', label: 'ROAS', fmt: fmtX },
];
const BUCKET_COLS_META = [
  { key: 'count', label: 'Creatives', fmt: fmtInt },
  { key: 'cost', label: 'Spend', fmt: fmtMoney },
  { key: 'ctr', label: 'CTR', fmt: (v) => fmtPct(v) },
  { key: 'conversions', label: 'Purchases', fmt: fmtInt },
  { key: 'roas', label: 'ROAS', fmt: fmtX },
];

const META_SORTS = {
  spend: (a, b) => b.spend - a.spend,
  roas: (a, b) => (b.roas || 0) - (a.roas || 0),
  purchases: (a, b) => b.purchases - a.purchases,
  ctr: (a, b) => (b.ctr || 0) - (a.ctr || 0),
};
const GOOGLE_SORTS = {
  impressions: (a, b) => b.impressions - a.impressions,
  ctr: (a, b) => (b.ctr || 0) - (a.ctr || 0),
  conversions: (a, b) => b.conversions - a.conversions,
  cvr: (a, b) => (b.cvr || 0) - (a.cvr || 0),
};

/** Lens üst çubuğundaki gibi bölmeli seçici. */
function Segmented({ options, value, onChange }) {
  return (
    <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${value === o.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const ShowMore = ({ hidden, onClick }) => (hidden > 0 ? (
  <button type="button" onClick={onClick} className="w-full px-4 py-2.5 text-xs font-medium text-gray-500 hover:bg-gray-50 border-t border-gray-100 flex items-center justify-center gap-1">
    Show {fmtInt(hidden)} more <ChevronDown className="w-3.5 h-3.5" />
  </button>
) : null);

const Th = ({ children, left }) => (
  <th className={`px-3 py-2 font-medium whitespace-nowrap ${left ? 'text-left px-4' : 'text-right'}`}>{children}</th>
);

export default function CreativeInsights() {
  const { selectedBrand } = useAuth();
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('meta');
  const [metaSort, setMetaSort] = useState('spend');
  const [googleSort, setGoogleSort] = useState('impressions');
  const [googleField, setGoogleField] = useState('ALL');
  const [metaLimit, setMetaLimit] = useState(PAGE_ROWS);
  const [googleLimit, setGoogleLimit] = useState(PAGE_ROWS);

  const fetchData = async (d = days, refresh = false) => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE}/api/creative-performance?days=${d}${refresh ? '&refresh=1' : ''}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      setData(await res.json());
      setMetaLimit(PAGE_ROWS);
      setGoogleLimit(PAGE_ROWS);
    } catch (e) {
      setError(e.message || 'Could not load data');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  const m = data?.meta;
  const g = data?.google;

  const metaAll = useMemo(() => [...(m?.creatives || [])].sort(META_SORTS[metaSort] || META_SORTS.spend), [m, metaSort]);
  const googleAll = useMemo(() => {
    let list = g?.assets || [];
    if (googleField !== 'ALL') list = list.filter((a) => a.fieldType === googleField);
    return [...list].sort(GOOGLE_SORTS[googleSort] || GOOGLE_SORTS.impressions);
  }, [g, googleField, googleSort]);

  // Bulgu kartlarında değer biçimi sorudan belli: Meta ROAS, Google oran.
  const findingFmt = (v) => (v.title.startsWith('Meta') ? fmtX : (x) => fmtPct(x));

  const warnings = [
    m?.error && `Meta: ${m.error}`,
    m?.textError && `Meta ad copy could not be loaded: ${m.textError}`,
    g?.error && `Google: ${g.error}`,
  ].filter(Boolean);

  const tabs = [
    { value: 'meta', label: `Meta creatives${m?.creatives?.length ? ` · ${fmtInt(m.creatives.length)}` : ''}` },
    { value: 'google', label: `Google copy${g?.assets?.length ? ` · ${fmtInt(g.assets.length)}` : ''}` },
    { value: 'patterns', label: 'Patterns' },
  ];

  return (
    <div className="min-h-full">
      {/* Üst çubuk — Glowy Lens ile aynı */}
      <div className="sticky top-0 z-30 bg-[#f6f8ff]/95 backdrop-blur border-b border-gray-200">
        <div className="px-4 md:px-6 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            <Lightbulb className="w-5 h-5 text-amber-500" />Creative &amp; Copy Insights
          </span>
          {data?.range && (
            <span className="text-xs text-gray-400 hidden sm:inline">
              {data.brand?.name} · {fmtDate(data.range.start)} – {fmtDate(data.range.end)}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Segmented
              options={PERIODS.map((p) => ({ value: p, label: `${p}d` }))}
              value={days}
              onChange={(v) => { setDays(v); fetchData(v); }}
            />
            <button
              type="button"
              onClick={() => fetchData(days)}
              disabled={loading}
              title="Reload"
              className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              onClick={() => fetchData(days, true)}
              disabled={loading}
              title="Pull fresh data from Google and Meta (skips the 12-hour cache)."
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
            >
              <Zap className="w-4 h-4" />Refresh live
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-5 max-w-[1500px] mx-auto space-y-5">
        {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        {loading && !data && (
          <Panel className="px-4 py-12 text-center">
            <RefreshCw className="w-5 h-5 text-gray-300 animate-spin mx-auto" />
            <div className="mt-3 text-sm text-gray-600">Pulling Meta ad copy and Google assets…</div>
            <div className="text-xs text-gray-400 mt-1">The first load can take 10–20 seconds.</div>
          </Panel>
        )}

        {!!warnings.length && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 space-y-0.5">
            {warnings.map((w) => <div key={w} className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{w}</div>)}
          </div>
        )}

        {data && (m?.creatives?.length || g?.assets?.length) ? (
          <>
            {/* Özet */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat label="Meta spend" value={fmtMoney(m?.totals?.spend)} sub={m?.totals ? `${fmtInt(m.totals.count)} creatives` : null} />
              <Stat label="Meta ROAS" value={fmtX(m?.totals?.spend ? m.totals.revenue / m.totals.spend : null)} sub={m?.totals ? `${fmtInt(m.totals.purchases)} purchases` : null} />
              <Stat label="Google impressions" value={fmtInt(g?.totals?.impressions)} sub={g?.totals ? `${fmtInt(g.totals.count)} text assets` : null} />
              <Stat label="Google CTR" value={fmtPct(g?.totals?.impressions ? g.totals.clicks / g.totals.impressions : null)} sub={g?.totals ? `${fmtInt(g.totals.conversions)} conversions` : null} />
            </div>

            {/* Bulgular */}
            {!!data.verdicts?.length && (
              <section>
                <h2 className="text-sm font-semibold text-gray-900 mb-2">Key findings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {data.verdicts.map((v) => <Finding key={v.title} v={v} fmt={findingFmt(v)} />)}
                </div>
              </section>
            )}

            <details className="group rounded-xl border border-gray-200 bg-white/60 px-4 py-2.5 text-xs text-gray-600">
              <summary className="cursor-pointer list-none flex items-center gap-1.5 text-gray-500 hover:text-gray-800">
                <Info className="w-3.5 h-3.5" />How findings are calculated
                <ChevronDown className="w-3.5 h-3.5 transition-transform group-open:rotate-180" />
              </summary>
              <div className="mt-2 space-y-1.5 leading-relaxed">
                <p>
                  Creatives are grouped by shared traits (catalog / static / video, copy theme, headline length) and
                  compared group against group. A group needs at least {fmtInt(data.thresholds?.impressions)} impressions
                  and {fmtMoney(data.thresholds?.spend)} spend before it can win; below that it is marked <em>low data</em>.
                </p>
                <p>
                  Meta performance comes from our database, ad copy is read live from the Meta Graph API. Google text
                  assets come straight from the Google Ads API. Results are cached for 12 hours.
                </p>
                {m?.textLimited && (
                  <p>
                    This brand has {fmtInt(m.totals.count)} Meta creatives; copy and pattern analysis uses the top{' '}
                    {fmtInt(m.textLimit)} by spend{m.truncated ? `, and the table lists the top ${fmtInt(m.rowLimit)}` : ''}.
                  </p>
                )}
                {!!m && m.textAvailable === 0 && !m.textError && (
                  <p>Meta ads for this brand carry no copy of their own (catalog ads pull text from the product), so compare Meta on visuals.</p>
                )}
              </div>
            </details>

            {/* Sekmeler */}
            <div className="flex flex-wrap items-center gap-2">
              <Segmented options={tabs} value={tab} onChange={setTab} />
            </div>

            {tab === 'meta' && (
              <div className="space-y-3">
                <SpendMix types={m?.types} />
                {m?.creatives?.length ? (
                  <Panel className="overflow-hidden">
                    <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                        <ImageIcon className="w-4 h-4 text-gray-400" />Meta creatives
                      </h3>
                      <Segmented
                        options={[{ value: 'spend', label: 'Spend' }, { value: 'roas', label: 'ROAS' }, { value: 'purchases', label: 'Purchases' }, { value: 'ctr', label: 'CTR' }]}
                        value={metaSort}
                        onChange={setMetaSort}
                      />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="text-xs text-gray-500 border-y border-gray-100 bg-gray-50/60">
                          <tr>
                            <Th left>Creative</Th><Th>Spend</Th><Th>Impr.</Th><Th>CTR</Th><Th>Purchases</Th><Th>CPA</Th><Th>ROAS</Th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {metaAll.slice(0, metaLimit).map((c) => {
                            const cc = cleanCreative(c);
                            const ts = TYPE_STYLE[c.type];
                            return (
                              <tr key={c.id} className="hover:bg-gray-50/70">
                                <td className="px-4 py-2.5">
                                  <div className="flex items-center gap-3">
                                    <a
                                      href={`${API_BASE}/api/creative-performance/image/${c.id}?brandId=${data.brand.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      title="Open full-size image"
                                    >
                                      <Thumb creativeId={c.id} brandId={data.brand.id} alt={cc.title} />
                                    </a>
                                    <div className="min-w-0 max-w-[460px]">
                                      <div className="flex items-center gap-1.5">
                                        <span className="font-medium text-gray-900 truncate" title={c.name || ''}>{cc.title}</span>
                                        {ts && <span className={`shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${ts.badge}`}>{ts.short}</span>}
                                      </div>
                                      {cc.text && <div className="text-xs text-gray-500 line-clamp-1" title={cc.text}>{cc.text}</div>}
                                      {cc.date && <div className="text-[11px] text-gray-400">Created {cc.date}</div>}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmtMoney(c.spend)}</td>
                                <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmtInt(c.impressions)}</td>
                                <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmtPct(c.ctr)}</td>
                                <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmtInt(c.purchases)}</td>
                                <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmtMoney(c.cpa)}</td>
                                <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-gray-900">{fmtX(c.roas)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <ShowMore hidden={metaAll.length - metaLimit} onClick={() => setMetaLimit(metaAll.length)} />
                  </Panel>
                ) : (
                  <Panel className="px-4 py-8 text-center text-sm text-gray-400">No Meta creatives in this period.</Panel>
                )}
              </div>
            )}

            {tab === 'google' && (
              g?.assets?.length ? (
                <Panel className="overflow-hidden">
                  <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                      <Type className="w-4 h-4 text-gray-400" />Google Ads text assets
                    </h3>
                    <div className="flex flex-wrap items-center gap-2">
                      <Segmented
                        options={[{ value: 'ALL', label: 'All' }, { value: 'HEADLINE', label: 'Headlines' }, { value: 'DESCRIPTION', label: 'Descriptions' }]}
                        value={googleField}
                        onChange={(v) => { setGoogleField(v); setGoogleLimit(PAGE_ROWS); }}
                      />
                      <Segmented
                        options={[{ value: 'impressions', label: 'Impr.' }, { value: 'ctr', label: 'CTR' }, { value: 'conversions', label: 'Conv.' }, { value: 'cvr', label: 'Conv. rate' }]}
                        value={googleSort}
                        onChange={setGoogleSort}
                      />
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="text-xs text-gray-500 border-y border-gray-100 bg-gray-50/60">
                        <tr>
                          <Th left>Text</Th><Th>Impr.</Th><Th>Clicks</Th><Th>CTR</Th><Th>Conv.</Th><Th>Conv. rate</Th><Th>Cost</Th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {googleAll.slice(0, googleLimit).map((a) => (
                          <tr key={`${a.id}-${a.fieldType}`} className="hover:bg-gray-50/70">
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-1.5 max-w-[620px]">
                                <span className="text-gray-900">{a.text || <span className="text-gray-400">{a.assetType}</span>}</span>
                              </div>
                              <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-400 max-w-[620px]">
                                <span className="px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-600 font-medium">
                                  {a.fieldType === 'HEADLINE' ? 'Headline' : a.fieldType === 'DESCRIPTION' ? 'Description' : a.fieldType}
                                </span>
                                {a.label && <span className="px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-medium">{a.label.toLowerCase()}</span>}
                                {a.length != null && <span>{a.length} chars</span>}
                                {!!a.campaigns?.length && <span className="truncate">· {a.campaigns.join(' · ')}</span>}
                              </div>
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmtInt(a.impressions)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmtInt(a.clicks)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-gray-900">{fmtPct(a.ctr)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmtInt(a.conversions)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmtPct(a.cvr)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmtMoney(a.cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <ShowMore hidden={googleAll.length - googleLimit} onClick={() => setGoogleLimit(googleAll.length)} />
                </Panel>
              ) : (
                <Panel className="px-4 py-8 text-center text-sm text-gray-400">No Google Ads text assets in this period.</Panel>
              )
            )}

            {tab === 'patterns' && (
              <div className="space-y-4">
                <p className="text-xs text-gray-500">
                  Shared traits, not individual creatives. One creative can fall into several themes (e.g. discount + call to action),
                  so rows don't add up — read each table as "which trait leads on which metric".
                </p>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
                  <BucketTable
                    title="Meta · creative type"
                    rows={m?.types}
                    columns={BUCKET_COLS_META_TYPE}
                    note="Catalog (DPA) ads take image and copy from the product, so they're kept apart from custom images and videos."
                  />
                  <BucketTable
                    title="Meta · video vs static (custom creatives only)"
                    rows={m?.formats}
                    columns={BUCKET_COLS_META}
                    note="Catalog ads are left out: we don't choose their image."
                  />
                  <BucketTable
                    title="Meta · copy theme"
                    rows={m?.themes}
                    columns={BUCKET_COLS_META}
                    note={m?.textAvailable === 0 ? 'No Meta copy for this brand (catalog ads).' : null}
                  />
                  <BucketTable title="Google · copy theme" rows={g?.themes} columns={BUCKET_COLS_GOOGLE} />
                  <BucketTable title="Google · headline length" rows={g?.headlineLength} columns={BUCKET_COLS_GOOGLE} />
                  <BucketTable title="Google · description length" rows={g?.descriptionLength} columns={BUCKET_COLS_GOOGLE} />
                  <BucketTable title="Google · headline vs description" rows={g?.fields} columns={BUCKET_COLS_GOOGLE} />
                </div>
              </div>
            )}
          </>
        ) : null}

        {data && !m?.creatives?.length && !g?.assets?.length && !loading && (
          <Panel className="px-4 py-12 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-gray-300" />
            No creative data for this brand in the selected period.
            <span className="text-xs text-gray-400">Needs a Meta or Google Ads connection with ads running.</span>
          </Panel>
        )}
      </div>
    </div>
  );
}
